import { useCallback, useEffect, useRef } from 'react';

import type {
  ApolloClient,
  DataState,
  DefaultContext,
  OperationVariables,
  TypedDocumentNode,
} from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client';
import type { SkipToken } from '@apollo/client/react';
import {
  skipToken,
  useLazyQuery as useLazyQueryFromApollo,
  useMutation as useMutationFromApollo,
  useQuery as useQueryFromApollo,
} from '@apollo/client/react';
import { useContextualData } from '@opendoor/observability';

import { useAuthAuthenticated } from 'platform/auth-fe';
import { useReauth } from 'platform/components/ReauthContext';
import navigationHelpers from 'platform/navigationHelpers';

import { isReAuthenticationRequired } from './Error';
import type { TEAM } from './teams';

// Captures the current call stack as a string, stripping the "Error" header and
// the first removeCount frames so the call site (the component or function that
// invoked the hook) appears as the first visible frame.
// Use removeCount=2 to strip captureStack + the direct apollo.ts wrapper.
// Use removeCount=3 when invoked through two apollo.ts helpers (e.g. useQuery
// called from useAuthenticatedQuery).
const captureStack = (removeCount: number): string => {
  const error = new Error();
  if (error.stack) {
    const [, ...frames] = error.stack.split('\n');
    return frames.slice(removeCount).join('\n');
  }

  return '';
};

// Simple helper for an empty object
type EmptyObject = Record<string, never>;

// Used to allow for optional options parameters w/Apollo Client v4's new typings
type OptionalVars<
  TOptions extends object | SkipToken,
  TVariables extends OperationVariables = EmptyObject,
> = EmptyObject extends TVariables ? [param?: TOptions | undefined] : [param: TOptions];

// The following four types exist to coerce results from Apollo Client v4 into either 'complete' or 'empty'
// dataStates. Those are the only two we use - we do not currently use 'streaming' (deferred queries)
// or 'partial' (partial data from cache) so this makes type narrowing simpler, all or nothing.

/**
 * Inject contextual data from Observability into Apollo's context.
 */
function useApolloContext(options?: { context?: DefaultContext }): DefaultContext {
  const context = useContextualData();

  return {
    ...options?.context,
    linkContext: {
      team: context.data.team as TEAM,
      ...options?.context?.linkContext,
    },
  };
}

type CompleteOrEmptyResult<
  TData,
  TVariables extends OperationVariables,
  TReturnVariables extends OperationVariables,
> = useQueryFromApollo.Result<TData, TVariables, 'empty' | 'complete', Partial<TReturnVariables>>;

const completeOrEmpty = <
  TData,
  TVariables extends OperationVariables,
  TReturnVariables extends OperationVariables = TVariables,
>(
  result: useQueryFromApollo.Result<
    TData,
    TVariables,
    DataState<TData>['dataState'],
    Partial<TReturnVariables>
  >,
): CompleteOrEmptyResult<TData, TVariables, TReturnVariables> => {
  const { data, dataState, ...rest } = result;

  if (dataState === 'complete') {
    return { data, dataState: 'complete', ...rest };
  }

  return { data: undefined, dataState: 'empty', ...rest };
};

type Authenticated = {
  /**
   * Internal flag used to indicate when useQuery is being invoked from useAuthenticatedQuery.
   *
   * Do not set this flag or stack traces won't match up on reported errors.
   */
  authenticated?: boolean;
  /**
   * used to allow passing options to forward authenticated for useQuery initialStack init
   *
   * Do not use this flag.
   */
  authenticatedSkip?: boolean;
};

type QueryOptions<TData, TVariables extends OperationVariables> =
  | (useQueryFromApollo.Options<TData, TVariables> & Authenticated)
  | SkipToken;

/**
 * A wrapper around Apollo Client's `useQuery`. Injects contextual data from Observability and
 * provides stack trace information when reporting errors via the client's link configuration.
 *
 * Pass `skipToken` as options to skip the query conditionally.
 *
 * Types are inferred from the TypedDocumentNode - do not pass explicit type arguments.
 */
export function useQuery<TData = unknown, TVariables extends OperationVariables = EmptyObject>(
  query: TypedDocumentNode<TData, TVariables>,
  ...[options]: OptionalVars<QueryOptions<TData, TVariables>, TVariables>
): useQueryFromApollo.Result<
  TData,
  TVariables,
  'empty' | 'complete',
  Partial<TVariables> & Authenticated
> {
  const authenticated = options !== skipToken && options?.authenticated;
  const initialStack = useRef(captureStack(authenticated ? 3 : 2)).current;
  const refetchStackRef = useRef<string | null>(null);

  const getStackTrace = useCallback(() => refetchStackRef.current ?? initialStack, [initialStack]);

  const resolvedOptions = options === skipToken ? undefined : options;
  const context = useApolloContext({
    context: { ...resolvedOptions?.context, getStackTrace },
  });

  const mergedOptions: QueryOptions<TData, TVariables> = {
    ...resolvedOptions,
    context,
    // Safe as we know options are valid options and context is a valid context
  } as QueryOptions<TData, TVariables>;

  // Safe: Apollo handles skipToken internally; we pass it through directly when set
  const result = useQueryFromApollo<TData, TVariables>(
    query,
    options === skipToken || options?.authenticatedSkip ? skipToken : mergedOptions,
  );

  const { refetch: originalRefetch } = result;
  const refetch: useQueryFromApollo.Result<
    TData,
    TVariables,
    'empty' | 'complete',
    Partial<TVariables>
  >['refetch'] = useCallback(
    async (variables?: Partial<TVariables>) => {
      // if this is set, reported errors will use this stack trace
      refetchStackRef.current = captureStack(2);

      try {
        return await originalRefetch(variables);
      } finally {
        refetchStackRef.current = null;
      }
    },
    [originalRefetch],
  );

  return completeOrEmpty({ ...result, refetch });
}

function getQueryName(query: string | undefined): string | null {
  const match = query?.match(/query\s+(\w+)/);
  return match ? match[1] : null;
}

type AuthQueryOptions<TData, TVariables extends OperationVariables> =
  | useQueryFromApollo.Options<TData, TVariables>
  | SkipToken;

export type QueryResult<
  TData = unknown,
  TVariables extends OperationVariables = EmptyObject,
> = useQueryFromApollo.Result<TData, TVariables, 'empty' | 'complete', Partial<TVariables>>;

/**
 * Wraps `useQuery` with an auth check.
 *
 * Pass `skipToken` as options to skip the query conditionally.
 *
 * Types are inferred from the TypedDocumentNode - do not pass explicit type arguments.
 */
export function useAuthenticatedQuery<
  TData = unknown,
  TVariables extends OperationVariables = EmptyObject,
>(
  query: TypedDocumentNode<TData, TVariables>,
  ...[options]: OptionalVars<AuthQueryOptions<TData, TVariables>, TVariables>
): useQueryFromApollo.Result<TData, TVariables, 'empty' | 'complete', Partial<TVariables>> {
  const authentication = useAuthAuthenticated(getQueryName(query.loc?.source?.body) ?? 'anonymous');
  const { showReauth } = useReauth();

  const skipping = options === skipToken || authentication?.state !== 'authenticated';

  const mergedOptions: QueryOptions<TData, TVariables> = {
    ...(skipping ? { authenticatedSkip: true } : options),
    authenticated: true,
    // Safe as we know options are valid options and context is a valid context
  } as QueryOptions<TData, TVariables>;

  const { error, ...rest } = useQuery(
    // eslint-disable-next-line all-restricted/apollo-require-document-node-identifier -- verified document via input params
    query,
    skipping ? skipToken : mergedOptions,
  );

  const lastReauthErrorRef = useRef<typeof error | null>(null);

  useEffect(() => {
    const isUnauthenticated = authentication?.state !== 'authenticated';
    const hasGqlErrors = error && CombinedGraphQLErrors.is(error);
    const hasReauthError =
      (hasGqlErrors &&
        error.errors.some(
          (err) =>
            err.message.includes('"code":"reauthentication-required"') ||
            isReAuthenticationRequired(err),
        )) ||
      error?.message.includes(`"code":"reauthentication-required"`);

    if (isUnauthenticated || !hasReauthError) {
      lastReauthErrorRef.current = null;
      return;
    }

    if (lastReauthErrorRef.current === error) return;

    lastReauthErrorRef.current = error;
    showReauth({
      title: 'Please sign in again to continue',
      onSuccess: () => navigationHelpers.hardPageReload(),
      onCancel: () => void authentication.logout(),
    });
  }, [authentication, error, showReauth]);

  return completeOrEmpty<TData, TVariables>({ error, ...rest });
}

/**
 * A re-export of Apollo Client's `useMutation` Options type.
 */
export type MutationHookOptions<
  TData,
  TVariables extends OperationVariables,
> = useMutationFromApollo.Options<TData, TVariables>;

/**
 * A re-export of Apollo Client's `useMutation` result type.
 */
export type UseMutationResult<TData = unknown> = useMutationFromApollo.Result<TData>;

/**
 * A re-export of Apollo Client's `mutate` result type with nullable data.
 */
export type MutateResult<TData = unknown> = ApolloClient.MutateResult<TData | null>;

/**
 * Wraps Apollo Client's `useMutation`. Inject contextual data from Observability and
 * provides stack trace information when reporting errors via the client's link configuration.
 *
 * Types are inferred from the TypedDocumentNode - do not pass explicit type arguments.
 */
export function useMutation<TData = unknown, TVariables extends OperationVariables = EmptyObject>(
  mutation: TypedDocumentNode<TData, TVariables>,
  options: MutationHookOptions<TData, TVariables> = {},
): useMutationFromApollo.ResultTuple<TData, TVariables> {
  const context = useApolloContext(options);
  const [originalMutate, result] = useMutationFromApollo<TData, TVariables>(mutation, {
    ...options,
    context,
  });

  const mutate: useMutationFromApollo.MutationFunction<TData, TVariables> = useCallback(
    (
      ...[mutationOptions]: OptionalVars<
        useMutationFromApollo.MutationFunctionOptions<TData, TVariables>,
        TVariables
      >
    ) => {
      const stackTrace = captureStack(2);
      const baseContext = mutationOptions?.context;
      const context: DefaultContext =
        typeof baseContext === 'function'
          ? (prev: DefaultContext) => ({ ...baseContext(prev), stackTrace })
          : { ...baseContext, stackTrace };

      return originalMutate(
        ...([{ ...mutationOptions, context }] as Parameters<typeof originalMutate>),
      );
    },
    [originalMutate],
  );

  return [mutate, result];
}

type LazyCompleteOrEmptyResult<TData, TVariables extends OperationVariables> = Extract<
  useLazyQueryFromApollo.Result<TData, TVariables>,
  { dataState: 'complete' | 'empty' }
>;

const lazyCompleteOrEmpty = <TData, TVariables extends OperationVariables>(
  result: useLazyQueryFromApollo.Result<TData, TVariables>,
): LazyCompleteOrEmptyResult<TData, TVariables> => {
  if (result.dataState === 'complete') {
    return result;
  }

  return { ...result, data: undefined, dataState: 'empty' };
};

/**
 * Wraps Apollo Client's `useLazyQuery`. Inject contextual data from Observability and
 * provides stack trace information when reporting errors via the client's link configuration.
 *
 * Types are inferred from the TypedDocumentNode - do not pass explicit type arguments.
 */
export function useLazyQuery<TData = unknown, TVariables extends OperationVariables = EmptyObject>(
  query: TypedDocumentNode<TData, TVariables>,
  options?: useLazyQueryFromApollo.Options<TData, TVariables>,
): useLazyQueryFromApollo.ResultTuple<TData, TVariables, 'empty' | 'complete'> {
  const [originalExecute, result] = useLazyQueryFromApollo(query, options);

  const context = useApolloContext();

  const execute: useLazyQueryFromApollo.ExecFunction<TData, TVariables> = useCallback(
    (
      ...[execOptions]: OptionalVars<useLazyQueryFromApollo.ExecOptions<TVariables>, TVariables>
    ) => {
      const mergedOptions: useLazyQueryFromApollo.ExecOptions<TVariables> = {
        ...execOptions,
        context: {
          ...execOptions?.context,
          linkContext: {
            team: context.linkContext?.team,
            ...execOptions?.context?.linkContext,
          },
          stackTrace: captureStack(2),
        },
        // Safe as we know options are valid options and context is a valid context
      } as useLazyQueryFromApollo.ExecOptions<TVariables>;

      return originalExecute(mergedOptions);
    },
    [context, originalExecute],
  );

  return [execute, lazyCompleteOrEmpty(result)];
}
